import type { createApplicationStartup } from '../../../shared/application-startup';
import type { RendererFailure } from '../../../shared/renderer-failure';

// Diagnostic messages can contain script URLs. Strip launch query/hash data
// before sending them; never serialize the page URL, Telegram SDK or storage.
export const sanitizeRendererDiagnostic = (value: string, limit: number) =>
  value.replace(/https?:\/\/[^\s)]+/gu, url => url.replace(/[?#].*/u, ''))
    .replace(/(?:tgWebAppData|initData|access_token|token|authorization|cookie)\s*[:=]\s*[^\s,;]+/giu, '[redacted]')
    .slice(0, limit);

export const buildBrowserRenderReport = (
  failure: RendererFailure,
  language: string,
  browser: Window = window,
  nav: Navigator = navigator,
  doc: Document = document,
) => {
  const asset = Array.from(doc.scripts).map(script => {
    try { return new URL(script.src).pathname; } catch { return ''; }
  }).find(path => /^\/assets\/main\.[a-z0-9]+\.js$/u.test(path)) ?? '';
  const page = browser.location.pathname;
  return {
    id: browser.crypto.randomUUID(),
    occurred_at: new Date().toISOString(),
    app_asset: asset,
    path: /^\/(?:oracle|journal|hexagrams|profile)\/?$/u.test(page) ? page : '/',
    runtime: browser.__NEIRO_MACOS__ ? 'macos' : browser.__NEIRO_RUNTIME__ ?? 'default',
    locale: language,
    environment: {
      user_agent: nav.userAgent.slice(0, 1024),
      platform: nav.platform.slice(0, 128),
      language: nav.language.slice(0, 64),
      languages: [...nav.languages].slice(0, 8).map(value => value.slice(0, 64)),
      hardware_concurrency: nav.hardwareConcurrency || 0,
      device_memory: (nav as Navigator & {deviceMemory?: number}).deviceMemory ?? 0,
      max_touch_points: nav.maxTouchPoints || 0,
      viewport_width: browser.innerWidth,
      viewport_height: browser.innerHeight,
      pixel_ratio: browser.devicePixelRatio || 1,
      screen_width: browser.screen.width,
      screen_height: browser.screen.height,
      color_depth: browser.screen.colorDepth,
      online: nav.onLine,
      secure_context: browser.isSecureContext,
      web_assembly: typeof WebAssembly !== 'undefined',
      reduced_motion: browser.matchMedia('(prefers-reduced-motion: reduce)').matches,
    },
    failure: {
      phase: failure.phase,
      message: sanitizeRendererDiagnostic(failure.message, 1024),
      stack: sanitizeRendererDiagnostic(failure.stack, 4096),
      contexts: failure.contexts.slice(0, 2).map(context => ({
        ...context, message: sanitizeRendererDiagnostic(context.message, 1024),
      })),
      attempts: failure.attempts,
      width: failure.width,
      height: failure.height,
      pixel_ratio: failure.pixelRatio,
      theme: failure.theme,
      mode: failure.mode,
    },
  };
};

/** One terminal graphics report per page, independent of the startup screen. */
export const observeBrowserRenderFailures = (
  startup: ReturnType<typeof createApplicationStartup>,
  language: () => string,
  send: typeof fetch = fetch,
  capture: typeof buildBrowserRenderReport = buildBrowserRenderReport,
) => {
  let reported = false;
  return startup.subscribe(() => {
    const failure = startup.getRendererFailure();
    if (reported || startup.getStatus() !== 'unsupported' || !failure) return;
    reported = true;
    // Reporting must never delay the explanation or cause another startup error.
    (async () => {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 5000);
      try {
        await send('/api/v1/diagnostics/render-failures', {
          method: 'POST', credentials: 'omit', keepalive: true,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(capture(failure, language())), signal: controller.signal,
        });
      } catch {
        // An offline browser cannot deliver diagnostics. No retry loop or UI overlay.
      } finally {
        clearTimeout(timeout);
      }
    })().catch(() => undefined);
  });
};
